<template>
  <div
    ref="wrapperRef"
    class="input-selector-wrapper position-relative"
    :class="{ disabled: disabled, 'is-open': showDropdown }">
    <input
      :value="displayValue"
      :placeholder="placeholder"
      :disabled="disabled"
      class="form-control border-0 py-3"
      autocomplete="off"
      @focus="handleFocus"
      @click="handleFocus"
      @input="handleInput" />
    <i class="fa-solid fa-chevron-down chevron-icon"></i>
    <Teleport to="body">
      <div
        v-if="showDropdown && !disabled"
        ref="dropdownRef"
        class="input-selector-dropdown dropdown-menu show"
        :style="dropdownStyle">
        <div
          v-for="option in filteredOptions"
          :key="option.code"
          class="dropdown-item"
          :class="{ active: option.code === modelValue }"
          @click="select(option)">
          {{ option.name }}
        </div>
        <div v-if="!filteredOptions.length" class="dropdown-item text-muted">{{ $t('general.sinResultados') }}</div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
  import { computed, ref, onBeforeMount, onBeforeUnmount } from 'vue';

  const props = defineProps({
    modelValue: { type: String, default: null },
    options: { type: Object, required: true },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
  });

  const emit = defineEmits(['update:modelValue']);

  const searchQuery = ref('');
  const showDropdown = ref(false);
  const wrapperRef = ref(null);
  const dropdownRef = ref(null);
  const dropdownStyle = ref({});

  const optionsList = computed(() => Object.entries(props.options).map(([code, name]) => ({ code, name })));

  const filteredOptions = computed(() => {
    if (!searchQuery.value) return optionsList.value;
    const query = searchQuery.value.toLowerCase();
    return optionsList.value.filter(opt => opt.name.toLowerCase().includes(query));
  });

  const displayValue = computed(() => {
    if (showDropdown.value) return searchQuery.value;
    return props.options[props.modelValue] || '';
  });

  function updateDropdownPosition() {
    const { bottom, left, width } = wrapperRef.value.getBoundingClientRect();
    dropdownStyle.value = {
      top: `${bottom + 4}px`,
      left: `${left}px`,
      width: `${width}px`,
    };
  }

  function select(option) {
    emit('update:modelValue', option.code);
    searchQuery.value = '';
    showDropdown.value = false;
  }

  function handleFocus() {
    if (props.disabled) return;

    searchQuery.value = '';
    showDropdown.value = true;
    updateDropdownPosition();
  }

  function handleInput(event) {
    if (props.disabled) return;
    searchQuery.value = event.target.value;
    showDropdown.value = true;
    updateDropdownPosition();
    if (!searchQuery.value) emit('update:modelValue', null);
  }

  function closeDropdown() {
    showDropdown.value = false;
    searchQuery.value = displayValue.value;
  }

  function handleClickOutside(e) {
    if (wrapperRef.value?.contains(e.target) || dropdownRef.value?.contains(e.target)) return;
    closeDropdown();
  }

  function handleScroll(e) {
    if (dropdownRef.value?.contains(e.target)) return;
    if (showDropdown.value) {
      closeDropdown();
    }
  }

  onBeforeMount(() => {
    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);

    if (props.modelValue) {
      searchQuery.value = props.options[props.modelValue];
    }
  });

  onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside);
    window.removeEventListener('scroll', handleScroll, true);
  });
</script>

<style lang="scss" scoped>
  .input-selector-wrapper {
    &.is-open {
      z-index: 1050;
    }

    &.disabled {
      opacity: 0.7;
    }

    .form-control {
      background-color: rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.02) !important;
      box-shadow: 0 0 3px 0 rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.35) inset !important;
      border: none !important;
      height: 40px !important;
      padding: 0.375rem 2.5rem 0.375rem 0.75rem !important;
      text-align: left !important;
      color: var(--landing-text-color, inherit) !important;
      transition: background-color 0.2s ease, box-shadow 0.2s ease !important;

      &:hover:not(:disabled) {
        background-color: rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.05) !important;
        box-shadow: 0 0 3px 0 rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.4) inset !important;
      }

      &:focus {
        outline: none !important;
        box-shadow: 0 0 3px 0 rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.35) inset !important;
      }

      &:disabled {
        background-color: var(--landing-background-color, var(--bs-white)) !important;
      }

      &::placeholder {
        color: rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.45) !important;
        opacity: 1;
      }
    }

    .chevron-icon {
      position: absolute;
      right: 0.75rem;
      top: 50%;
      transform: translateY(-50%);
      color: var(--landing-text-color, var(--bs-primary));
      pointer-events: none;
      font-size: 14px;
      z-index: 10;
    }
  }

  .input-selector-dropdown {
    position: fixed;
    z-index: 10000;
    max-height: 300px;
    overflow-y: auto;
    background-color: var(--bs-white);
    border: 1px solid rgba(var(--bs-primary-rgb), 0.25) !important;
    border-radius: var(--bs-border-radius) !important;
    box-shadow: 0 4px 6px rgba(var(--bs-black-rgb), 0.1) !important;
    padding: 0;

    .dropdown-item {
      padding: 12px 16px;
      cursor: pointer;
      transition: background-color 0.2s ease !important;
      border-bottom: 1px solid rgba(var(--bs-primary-rgb), 0.1);

      &:hover {
        background-color: rgba(var(--bs-primary-rgb), 0.1) !important;
      }

      &.active {
        background-color: rgba(var(--bs-primary-rgb), 0.15) !important;
        font-weight: 600;
        color: var(--bs-primary) !important;
      }

      &:last-child {
        border-bottom: none;
      }
    }
  }

  @media screen and (max-width: 768px) {
    .input-selector-wrapper .form-control,
    .input-selector-dropdown .dropdown-item {
      font-size: 13px !important;
    }
  }
</style>
